"use client"
import {
  FlexCenter,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  ModalHeaderTitle,
  type ModalProps,
  NumberDisplay,
  TextBody,
  TextDisplay,
  VisuallyHidden,
} from "@opensea/ui-kit"
import { Error } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { OFFER_MAX_DECIMALS } from "../../../constants"
import type { CreateCollectionOfferFormValues } from "../../types"

type CollectionOfferPriceModalProps = Pick<ModalProps, "open"> & {
  setIsOpen: (isOpen: boolean) => void
  onNext: () => void
  offerSymbol: string
}

export const CollectionOfferPriceWarningModal = ({
  open,
  onNext,
  setIsOpen,
  offerSymbol,
}: CollectionOfferPriceModalProps) => {
  const t = useTranslations("wallet.CollectionOfferPriceWarningModal")
  const { control } = useFormContext<CreateCollectionOfferFormValues>()
  const offers = useWatch({ control, name: "offers" })
  const highSevPriceWarnings = offers.filter(offer => offer?.shouldConfirmPrice)

  if (highSevPriceWarnings.length === 0) {
    return null
  }

  const firstWarningOffer = highSevPriceWarnings[0]

  return (
    <Modal
      content={
        <>
          <ModalHeader>
            <VisuallyHidden>
              <ModalHeaderTitle>{t("title")}</ModalHeaderTitle>
            </VisuallyHidden>
          </ModalHeader>
          <ModalBody className="flex-col items-center gap-3">
            <FlexCenter className="rounded-full bg-error-1-transparent-1 p-3">
              <Error fill="error-1" size={28} />
            </FlexCenter>
            <TextDisplay size="sm">{t("highOfferPrice")}</TextDisplay>
            <TextBody className="text-center text-secondary" size="md">
              {t.rich("warningMessage", {
                price: () => (
                  <NumberDisplay
                    className="text-text-primary"
                    suffix={offerSymbol}
                    value={firstWarningOffer?.price}
                  />
                ),
                floorPrice: () => (
                  <NumberDisplay
                    className="text-text-primary"
                    suffix={offerSymbol}
                    value={
                      firstWarningOffer?.floorPrice &&
                      bn(firstWarningOffer.floorPrice).decimalPlaces(
                        OFFER_MAX_DECIMALS,
                      )
                    }
                  />
                ),
              })}
            </TextBody>
          </ModalBody>
          <ModalFooter>
            <ModalFooterButton
              onClick={() => {
                setIsOpen(false)
              }}
              variant="secondary"
            >
              {t("cancel")}
            </ModalFooterButton>
            <ModalFooterButton
              onClick={() => {
                setIsOpen(false)
                onNext()
              }}
            >
              {t("confirmOfferPrice")}
            </ModalFooterButton>
          </ModalFooter>
        </>
      }
      onOpenChange={setIsOpen}
      open={open}
    />
  )
}
